// Utility functions for handling product URLs and variants

// Friendly name mapping for variants
export const variantNameMap: Record<string, string> = {
    'desertpeach': '674ea96a197065b8a54a3236',
    'forestgreen': '674ea9b9197065b8a54a3237',
    'arcticblue': '674ea943197065b8a54a3235',
    'customcolours': '674b4a1f197065b8a54a3224',
    'custom': '674b4a1f197065b8a54a3224',
    'butterpopcorn': '6830b0df43132554b41a6b70',
    'butter': '6830b0df43132554b41a6b70',
    'midnightberry': '683703f89b03e80f20e8bfb8'
}

// Reverse mapping - ID to friendly name (with preferred defaults)
export const variantIdToNameMap: Record<string, string> = {
    '674ea96a197065b8a54a3236': 'desertpeach',
    '674ea9b9197065b8a54a3237': 'forestgreen',
    '674ea943197065b8a54a3235': 'arcticblue',
    '674b4a1f197065b8a54a3224': 'customcolours',
    '6830b0df43132554b41a6b70': 'butterpopcorn', // Default to full name, not 'butter'
    '683703f89b03e80f20e8bfb8': 'midnightberry'
}

/**
 * Convert variant name to ID
 */
export const getVariantIdFromName = (variantName: string): string | null => {
    const cleanName = variantName.toLowerCase().replace(/[^a-z0-9]/g, '')
    return variantNameMap[cleanName] || null
}

/**
 * Convert variant ID to friendly name
 */
export const getVariantNameFromId = (variantId: string): string | null => {
    return variantIdToNameMap[variantId] || null
}

/**
 * Check if a string is a valid MongoDB ObjectId format
 */
export const isValidObjectId = (id: string): boolean => {
    return /^[0-9a-fA-F]{24}$/.test(id)
}

/**
 * Generate a clean product URL with variant
 */
export const generateProductUrl = (
    region: string,
    variantId: string,
    useFriendlyName = true,
    preserveQuery?: string
): string => {
    const friendlyName = useFriendlyName ? getVariantNameFromId(variantId) : null
    const variant = friendlyName || variantId
    const baseUrl = `/${region}/products/wired-headphones-1/${variant}`

    if (preserveQuery) {
        // Clean up the query string to avoid double question marks
        const cleanQuery = preserveQuery.startsWith('?') ? preserveQuery : `?${preserveQuery}`
        return `${baseUrl}${cleanQuery}`
    }

    return baseUrl
}

/**
 * Parse variant from URL parameters
 */
export const parseVariantFromUrl = (
    pathVariant?: string,
    queryVariant?: string | null,
    defaultId = "683703f89b03e80f20e8bfb8"
): string => {
    // First try path parameter
    if (pathVariant) {
        // Check if it's a friendly name
        const friendlyId = getVariantIdFromName(pathVariant)
        if (friendlyId) return friendlyId

        // Check if it's a valid ObjectId format
        if (isValidObjectId(pathVariant)) return pathVariant
    }

    // Then try query parameter
    if (queryVariant) {
        // Check if it's a friendly name
        const friendlyId = getVariantIdFromName(queryVariant)
        if (friendlyId) return friendlyId

        // Check if it's a valid ObjectId format
        if (isValidObjectId(queryVariant)) return queryVariant
    }

    return defaultId
}

/**
 * Clean up UTM parameters and other query params to avoid double question marks
 */
export const cleanUrlParams = (url: string): string => {
    // Handle cases like "?variant=xyz?utm_source=..."
    // Split by '?' and rejoin properly
    const parts = url.split('?')
    if (parts.length <= 2) return url

    // First part is the base URL, rest are query parameters
    const baseUrl = parts[0]
    const queryParts = parts.slice(1).join('&') // Join with & instead of ?

    return `${baseUrl}?${queryParts}`
}
